import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import LearnMegaMenu from "@/components/LearnMegaMenu";
import ResourcesMegaMenu from "@/components/ResourcesMegaMenu";
import CommunityMegaMenu from "@/components/CommunityMegaMenu";
import { Menu, ChevronRight, Video, Layers, FileText, BookOpen, Globe, BookMarked } from "lucide-react";
import depinLogo from "@/assets/depin-logo-new.png";
import mobileLogo from "@/assets/mobile-logo.png";

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 px-4 py-3 md:py-4 md:flex md:justify-center transition-all duration-300 ${isScrolled ? "bg-background/90 backdrop-blur-md border-b border-border/50 md:bg-transparent md:border-0" : ""}`}>
      {/* Mobile Header - Logo left, Menu right */}
      <div className="md:hidden flex items-center justify-between w-full">
        <Link to="/" className="flex items-center">
          <img src={mobileLogo} alt="DePIN Land Logo" className="h-7 w-auto" />
        </Link>
        
        <Sheet open={isOpen} onOpenChange={setIsOpen}>
          <SheetTrigger asChild>
            <Button 
              variant="ghost" 
              size="icon" 
              className="rounded-full h-10 w-10 bg-card/80 backdrop-blur-md border border-border/50"
            >
              <Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-[280px] sm:w-[320px] bg-card/95 backdrop-blur-xl border-border p-0">
            {/* Header with logo */}
            <div className="p-4 border-b border-border/50">
              <SheetHeader>
                <SheetTitle className="text-left">
                  <Link to="/" onClick={() => setIsOpen(false)} className="flex items-center hover:opacity-80 transition-opacity">
                    <img src={mobileLogo} alt="DePIN Land Logo" className="h-7 w-auto" />
                  </Link>
                </SheetTitle>
              </SheetHeader>
            </div>

            {/* Scrollable menu content */}
            <div className="flex-1 overflow-y-auto p-4">
              <div className="flex flex-col gap-1">
                <Link to="/ventures" onClick={() => setIsOpen(false)} className="group flex items-center justify-between p-3 rounded-xl text-foreground hover:bg-muted/30 transition-all duration-300">
                  <span className="font-medium text-sm">Ventures</span>
                  <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                </Link>

                <div className="mt-4 space-y-1">
                  <div className="font-mono text-muted-foreground px-3 text-xs uppercase tracking-widest mb-2">Learn</div>
                  <Link to="/videos" onClick={() => setIsOpen(false)} className="group flex items-center gap-3 p-3 rounded-xl hover:bg-muted/30 transition-all duration-300">
                    <div className="p-2 rounded-lg bg-muted/50 group-hover:bg-background transition-colors">
                      <Video className="w-4 h-4 text-foreground" />
                    </div>
                    <div>
                      <div className="font-medium text-foreground text-sm">Videos</div>
                      <p className="text-xs text-muted-foreground">DePIN implementations</p>
                    </div>
                  </Link>
                  <Link to="/ecosystem" onClick={() => setIsOpen(false)} className="group flex items-center gap-3 p-3 rounded-xl hover:bg-muted/30 transition-all duration-300">
                    <div className="p-2 rounded-lg bg-muted/50 group-hover:bg-background transition-colors">
                      <Layers className="w-4 h-4 text-foreground" />
                    </div>
                    <div>
                      <div className="font-medium text-foreground text-sm">Ecosystem</div>
                      <p className="text-xs text-muted-foreground">DePIN Land ecosystem</p>
                    </div>
                  </Link>
                </div>

                <div className="mt-4 space-y-1">
                  <div className="font-mono text-muted-foreground px-3 text-xs uppercase tracking-widest mb-2">Resources</div>
                  <a
                    href="https://depin-land.gitbook.io/depin-land"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setIsOpen(false)}
                    className="group flex items-center gap-3 p-3 rounded-xl hover:bg-muted/30 transition-all duration-300"
                  >
                    <div className="p-2 rounded-lg bg-muted/50 group-hover:bg-background transition-colors">
                      <FileText className="w-4 h-4 text-foreground" />
                    </div>
                    <div>
                      <div className="font-medium text-foreground text-sm">Documentation</div>
                      <p className="text-xs text-muted-foreground">Mission and vision</p>
                    </div>
                  </a>
                  <Link to="/blog" onClick={() => setIsOpen(false)} className="group flex items-center gap-3 p-3 rounded-xl hover:bg-muted/30 transition-all duration-300">
                    <div className="p-2 rounded-lg bg-muted/50 group-hover:bg-background transition-colors">
                      <BookOpen className="w-4 h-4 text-foreground" />
                    </div>
                    <div>
                      <div className="font-medium text-foreground text-sm">Blog</div>
                      <p className="text-xs text-muted-foreground">Latest insights</p>
                    </div>
                  </Link>
                </div>

                <div className="mt-4 space-y-1">
                  <div className="font-mono text-muted-foreground px-3 text-xs uppercase tracking-widest mb-2">Community</div>
                  <Link to="/about" onClick={() => setIsOpen(false)} className="group flex items-center gap-3 p-3 rounded-xl hover:bg-muted/30 transition-all duration-300">
                    <div className="p-2 rounded-lg bg-muted/50 group-hover:bg-background transition-colors">
                      <Globe className="w-4 h-4 text-foreground" />
                    </div>
                    <div>
                      <div className="font-medium text-foreground text-sm">About Us</div>
                      <p className="text-xs text-muted-foreground">Who we are</p>
                    </div>
                  </Link>
                </div>
              </div>
            </div>

            {/* Fixed bottom CTA - Same style as desktop DePIN Library button */}
            <div className="p-4 border-t border-border/50 bg-card/95">
              <Button 
                onClick={() => setIsOpen(false)}
                className="w-full group relative rounded-xl bg-gradient-to-r from-primary to-lime-500 text-primary-foreground hover:from-lime-500 hover:to-primary py-5 text-base font-medium shadow-[0_0_20px_rgba(163,230,53,0.3)] hover:shadow-[0_0_30px_rgba(163,230,53,0.5)] transition-all duration-500 border-0 overflow-hidden gap-2"
              >
                <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-700" />
                <BookMarked className="h-4 w-4 relative z-10 transition-transform duration-300 group-hover:rotate-12" />
                <span className="relative z-10">DePIN Library</span>
              </Button>
            </div>
          </SheetContent>
        </Sheet>
      </div>

      {/* Desktop Floating Pill Navigation */}
      <div className={`hidden md:flex items-center gap-2 px-4 py-2 rounded-full transition-all duration-500 ease-out ${isScrolled ? "bg-card/90 backdrop-blur-sm shadow-lg border border-border scale-[0.98]" : "bg-card/80 backdrop-blur-md border border-border/50 scale-100"}`} style={{
        boxShadow: isScrolled ? '0 0 20px rgba(var(--primary-rgb, 59, 130, 246), 0.15), 0 4px 20px rgba(0, 0, 0, 0.1)' : 'none'
      }}>
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 pr-4 border-r border-border/50">
          <img src={depinLogo} alt="DePIN Land Logo" className="h-8 w-auto" width={28} height={32} />
          <span className="font-bold text-foreground text-lg tracking-tight">DePIN Land</span>
        </Link>

        {/* Desktop Navigation Links */}
        <div className="flex items-center gap-1">
          <Link to="/ventures" className="px-4 py-2 text-sm font-medium text-foreground hover:text-primary transition-colors rounded-full hover:bg-muted/50">
            Ventures
          </Link>

          {/* Learn Mega Menu */}
          <LearnMegaMenu />

          {/* Resources Mega Menu */}
          <ResourcesMegaMenu />

          {/* Community Mega Menu */}
          <CommunityMegaMenu />
        </div>

        {/* CTA Button */}
        <Button className="group relative rounded-full px-5 py-2 text-sm font-medium bg-gradient-to-r from-primary to-lime-500 hover:from-lime-500 hover:to-primary text-primary-foreground shadow-[0_0_20px_rgba(163,230,53,0.3)] hover:shadow-[0_0_30px_rgba(163,230,53,0.5)] transition-all duration-500 border-0 overflow-hidden gap-2">
          <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-700" />
          <BookMarked className="h-4 w-4 relative z-10 transition-transform duration-300 group-hover:rotate-12" />
          <span className="relative z-10">DePIN Library</span>
        </Button>
      </div>
    </nav>
  );
};

export default Navigation;